import { useEffect, useState } from 'react';
import {
  SecurityAsset,
  SecurityMutualFundDetails,
  useGetMfSecurityBuyScreenInformationQuery,
  V1MarketSide,
} from '@fintronners/react-api/src';
import Image from 'next/image';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/common/Select';
import { Button } from '@/components/common/Button';
import { AssetOverview } from '@/components/common/AssetOverview';
import { OrderTypeDialog } from '@/components/features/trade/OrderTypeDialog';
import { SheetContent, SheetFooter, SheetTitle } from '@/components/common/Sheet';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { TradeFormData, TradeSide } from './schema';
import { UseExternalAccount } from '@fintronners/react-widgets/src/hooks/useExternalAccounts';
import { NumberNoNaN, rawThousandFormatNumbro } from '@fintronners/react-utils/src/numberUtilsTSX';
import { TradeForm } from './parts/TradeForm';
import { WebBodyText20, WebCaption12 } from '@fintronners/react-ui/src/GlobalStyling/webTypography';
import { InfoIcon } from '@/assets/icons';
import useFeatureFlags from '@/hooks/config/useFeatureFlags';
import MutualFundDetailsWidget from '@/components/features/securities/mutualFund/MutualFundDetailsWidget/MutualFundsDetailsWidget';
import { FriendlyReminderDialog } from '@/components/features/trade/FriendlyReminderDialog/FriendlyReminderDialog';
import { MutualFundSellCautionDialog } from '@/components/features/trade/MutualFundSellCautionDialog/MutualFundSellCautionDialog';
import { useTranslation } from 'react-i18next';
import useTradeMinAmountLimit from '@fintronners/internal-core/src/trade/hooks/useTradeMinAmountLimit';
import { getTradeAmountErrorMessage } from '@fintronners/internal-core/src/trade/utils';
import FinTronConfig from '@fintronners/react-config/src/Config/FinTronConfig';
import { OrderType, QuantityType } from '@fintronners/internal-core/src/trade/constants';
import useTradeApiService from '@/hooks/api/useTradeService';
import usePreviewTradeFee from '@fintronners/react-widgets/src/hooks/trades/usePreviewTradeFee';
import useUserOwnsSecurity from '@fintronners/react-widgets/src/hooks/trades/useUserOwnsSecurity';
import useIsMutualFundClosed from '@fintronners/react-widgets/src/hooks/trades/useIsMutualFundClosed';
import { usePreviewTradeFeeStore } from '@/stores/trades/preview-trade-fee';
import useGrowthBookFeature from '@fintronners/react-config/src/hooks/useGrowthBookFeature';
import { FeatureFlags } from '@fintronners/react-config/src/Config/growthbook';
import type { TradeBreakdown } from '@/hooks/trade/useTradeBreakdown';
import { useSidePanel } from '@/context/SidePanelContext';
import { TradeFormSteps } from './constants';

interface TradeSidePanelFormProps {
  side: TradeSide;
  asset: SecurityAsset;
  tradeBreakdown: TradeBreakdown;
  account?: UseExternalAccount | null;
  loading?: boolean;
  onContinue: () => void;
  isMutualFund?: boolean;
  isBond?: boolean;
}

const displayStrings = {
  withdrawalThreshold: (amount: string) =>
    `Sell orders of $${amount} or greater will automatically Sell All.`,
  withdrawalPortfolioNote:
    'If your sell is 90% or greater of your mutual fund holding, then we will automatically Sell All of the holding.',
  buy: 'Buy',
  sell: 'Sell',
  tradeMarketForm: 'Trade market form',
  marketBuy: 'Market buy',
  limitBuy: 'Limit buy',
  repeatInvestment: 'Repeat investment',
  marketSell: 'Market sell',
  stopLoss: 'Stop loss',
  continue: 'Continue',
};

export const TradeSidePanelForm = ({
  side,
  asset,
  tradeBreakdown,
  account,
  loading,
  onContinue,
  isMutualFund,
}: TradeSidePanelFormProps) => {
  const { featureFlags: ff, tenants } = useFeatureFlags();

  const { sellMin } = FinTronConfig.selfDirected.trades;

  const { goTo } = useSidePanel();
  const goToErrorPanel = () => goTo(TradeFormSteps.ERROR);

  const { t } = useTranslation();
  const [isAccountValid, setIsAccountValid] = useState(true);
  const { control, setValue, setError } = useFormContext<TradeFormData>();
  const [orderType, rawLimitPrice, rawStopLoss, interval] = useWatch({
    control,
    name: ['orderType', 'limitPrice', 'stopLoss', 'interval'],
  });

  const limitPrice =
    orderType === OrderType.STOP_LOSS ? NumberNoNaN(rawStopLoss) : NumberNoNaN(rawLimitPrice);

  const {
    loading: isPreviewTradeFeeLoading,
    setData: setPreviewTradeFeeData,
    setError: setPreviewTradeFeeError,
    setLoading: setPreviewTradeFeeLoading,
  } = usePreviewTradeFeeStore();

  const shouldCheckMutualFundClosed = isMutualFund && tenants.isAlight;

  const { ownsSecurity: ownsMutualFundSecurity } = useUserOwnsSecurity({
    securityId: asset?.id,
    skip: !shouldCheckMutualFundClosed,
  });

  const isMutualFundClosed = useIsMutualFundClosed(
    shouldCheckMutualFundClosed ? asset?.symbol : undefined,
  );

  const isFeesAndCommissionsEnabled = useGrowthBookFeature(
    FeatureFlags.isFeesAndCommissionsEnabled,
  );

  useEffect(() => {
    if (isMutualFund && orderType === OrderType.MARKET_SELL) {
      setValue('orderType', OrderType.MARKET_SELL);
    }
  }, [isMutualFund, setValue, orderType]);

  const isLimitBuy = orderType === OrderType.LIMIT_BUY;
  const isRepeatInvestment = orderType === OrderType.REPEAT_INVESTMENT;
  const isStopLoss = orderType === OrderType.STOP_LOSS;

  const {
    quantityType,
    shareAmount,
    dollarAmount,
    grossOrderAmount,
    netOrderAmount,
    sellingPercentage,
    isSellingAll,
    shareBalance,
    shareBalanceInDollars,
    shareBalanceInDollarsAtLimitPrice,
    tradableInCash,
  } = tradeBreakdown;

  const currentAssetPrice = asset?.market?.last;
  const isFractionable = asset?.isFractionable ?? true;
  const isStopLossDisabled =
    !limitPrice ||
    limitPrice > currentAssetPrice ||
    grossOrderAmount > shareBalanceInDollarsAtLimitPrice;

  const securityID = asset?.id;

  const onRequestInvoked = () => setPreviewTradeFeeLoading(true);
  const onRequestEnd = () => setPreviewTradeFeeLoading(false);
  const onError = (error: Error) => {
    // Reset amount to 0 to prevent a loop that keeps redirecting to the error screen on fee errors
    setValue('amount', '0');

    setPreviewTradeFeeError(error);
    goToErrorPanel();
  };

  const shouldSkipTradeFeeRequest = !securityID || !isFeesAndCommissionsEnabled;

  const tradeApiService = useTradeApiService();
  const { getTradeFee, abortGetTradeFee } = usePreviewTradeFee({
    withDebounce: true,
    shouldSkip: shouldSkipTradeFeeRequest,
    apiService: tradeApiService,
    onSuccess: setPreviewTradeFeeData,
    onError,
    onRequestEnd,
    onRequestInvoked,
  });

  useEffect(() => {
    // It may be tempting to skip the request when there are validation errors,
    // but we were having issues when the amount + fees exeeded the available cash.
    //
    // Since this `useEffect` would need to depend on the `hasValidationError`,
    // every time it changes the effect would be triggered, causing this loop:
    // 1. `hasValidationError` -> `true`
    // 2. `useEffect` -> resets the fee data (resulting in a valid amount)
    // 3. `hasValidationError` -> `false`
    // 4. `useEffect` -> perform the request with the same amount that triggered the error
    // 5. request ends and updates the fee data (which results in invalid total amount again)
    // 6. Back to "1.", closing the loop
    //
    // And if we simply abort the request and don't reset fee data here, the user
    // would see stale fees that may not apply to the current amount when there is
    // a transition from valid -> invalid amount.
    if (grossOrderAmount === 0) {
      abortGetTradeFee();
      setPreviewTradeFeeData(null);
      return;
    }

    getTradeFee({
      amount: grossOrderAmount.toString(),
      assetUid: securityID || '',
      side: side === TradeSide.BUY ? V1MarketSide.Buy : V1MarketSide.Sell,
    });
  }, [side, securityID, grossOrderAmount, getTradeFee, abortGetTradeFee, setPreviewTradeFeeData]);

  const minimumTradeAmount = useTradeMinAmountLimit(side, securityID);

  // When the user is attempting to liquidate their position (sell all), we
  // must not require the minimum amount
  const isAmountUnderMin = !isSellingAll && grossOrderAmount < minimumTradeAmount;
  const isAmountOverMax = grossOrderAmount > tradableInCash;
  const isAmountInvalid =
    (quantityType === QuantityType.DOLLARS && !dollarAmount) ||
    (quantityType === QuantityType.SHARES && !shareAmount) ||
    isAmountUnderMin ||
    isAmountOverMax;
  const isLimitPriceInvalid = isLimitBuy && !limitPrice;
  const isStopLossInvalid = isStopLoss && isStopLossDisabled;
  const isRepeatInvestmentInvalid = isRepeatInvestment && (!interval || !account);

  const errorMessage = getTradeAmountErrorMessage(
    side,
    grossOrderAmount,
    netOrderAmount,
    shareAmount,
    tradableInCash,
    minimumTradeAmount,
    isMutualFund,
    isMutualFundClosed,
    ownsMutualFundSecurity,
  );

  const hasValidationError = Boolean(errorMessage);

  const isContinueDisabled =
    hasValidationError ||
    isAmountInvalid ||
    isLimitPriceInvalid ||
    isStopLossInvalid ||
    isRepeatInvestmentInvalid ||
    !isAccountValid ||
    isPreviewTradeFeeLoading;

  const { data: mfSecurityData, loading: loadingMfSecurityData } =
    useGetMfSecurityBuyScreenInformationQuery({
      variables: { securityID },
      skip: !securityID,
    });

  const mfDetails = mfSecurityData?.securityAssets.edges?.find(
    (edge) => edge?.node?.id === asset?.id,
  )?.node?.details as SecurityMutualFundDetails;

  const [friendlyReminderProps, setFriendlyReminderProps] = useState<{
    sellMin: number;
    leftoverCash: number;
    securitySymbol: string;
  }>({ leftoverCash: 0, securitySymbol: '', sellMin: 0 });
  const [showFriendlyReminder, setShowFriendlyReminder] = useState(false);
  const [showMutualFundSellCaution, setShowMutualFundSellCaution] = useState(false);
  const [leftoverCashConfirmed, setLeftoverCashConfirmed] = useState(false);

  useEffect(() => {
    if (leftoverCashConfirmed) {
      onContinue();
    }
  }, [leftoverCashConfirmed, onContinue]);

  useEffect(() => {
    setError('amount', {
      message: errorMessage,
    });
  }, [errorMessage, setError]);

  const handleOnContinue = () => {
    if (orderType === OrderType.MARKET_SELL) {
      if (isMutualFund) {
        if (sellingPercentage >= 90 && sellingPercentage < 100) {
          setShowMutualFundSellCaution(true);
          return;
        }
      } else {
        const leftoverCash = shareBalanceInDollars - dollarAmount;

        if (leftoverCash < sellMin && leftoverCash !== 0 && !leftoverCashConfirmed) {
          setFriendlyReminderProps({ securitySymbol: asset.symbol, leftoverCash, sellMin });
          setShowFriendlyReminder(true);
          return;
        }
      }
    } else if (orderType === OrderType.STOP_LOSS) {
      const leftoverCash = shareBalanceInDollarsAtLimitPrice - dollarAmount;

      if (leftoverCash < sellMin && leftoverCash !== 0 && !leftoverCashConfirmed) {
        setFriendlyReminderProps({ securitySymbol: asset.symbol, leftoverCash, sellMin });
        setShowFriendlyReminder(true);
        return;
      }
    }

    onContinue();
  };

  const handleSellAllClick = () => {
    if (isMutualFund) {
      setValue('amount', tradableInCash.toString());
      onContinue();
      return;
    }

    if (!isFractionable) {
      setValue('shares', shareBalance.toString());
      onContinue();
      return;
    }

    setValue('quantityType', QuantityType.DOLLARS);
    setValue(
      'amount',
      orderType === OrderType.STOP_LOSS
        ? shareBalanceInDollarsAtLimitPrice.toString()
        : shareBalanceInDollars.toString(),
    );
    onContinue();
  };

  useEffect(() => {
    if (orderType !== OrderType.REPEAT_INVESTMENT) {
      return;
    }

    setValue('quantityType', QuantityType.DOLLARS);
  }, [orderType]);

  return (
    <SheetContent>
      {side === TradeSide.SELL && (
        <>
          <FriendlyReminderDialog
            {...friendlyReminderProps}
            open={showFriendlyReminder}
            onOpenChange={setShowFriendlyReminder}
            onKeepPressed={() => {
              setLeftoverCashConfirmed(true);
            }}
            onSellAllPressed={handleSellAllClick}
          />
          {isMutualFund && (
            <MutualFundSellCautionDialog
              tradableInCash={tradableInCash}
              open={showMutualFundSellCaution}
              onOpenChange={setShowMutualFundSellCaution}
              onSellAllPressed={handleSellAllClick}
            />
          )}
        </>
      )}
      <div className="mt-4 flex flex-col gap-7">
        <div>
          <AssetOverview
            basePrice={asset?.market?.previousDayClose}
            price={currentAssetPrice}
            logoURI={asset?.logoURI}
            symbol={asset?.symbol}
            name={asset?.name}
            loading={loading}
          />
        </div>
        <div>
          <SheetTitle className="sr-only">{displayStrings.tradeMarketForm}</SheetTitle>
          {isMutualFund ? (
            <WebBodyText20.Bold className="bg-grey6 text-grey72 inline-block rounded-lg px-[10px] py-[5px]">
              {side === TradeSide.BUY ? displayStrings.buy : displayStrings.sell}
            </WebBodyText20.Bold>
          ) : (
            <Controller
              control={control}
              name="orderType"
              render={({ field: { value, onChange } }) => (
                <Select value={value} defaultValue={value} onValueChange={onChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {side === TradeSide.BUY ? (
                        <>
                          <SelectItem value={OrderType.MARKET_BUY}>
                            {displayStrings.marketBuy}
                          </SelectItem>
                          <SelectItem value={OrderType.LIMIT_BUY}>
                            {displayStrings.limitBuy}
                          </SelectItem>
                          {!ff.shouldHideTradeRepeatInvestment && isFractionable && (
                            <SelectItem value={OrderType.REPEAT_INVESTMENT}>
                              {displayStrings.repeatInvestment}
                            </SelectItem>
                          )}
                        </>
                      ) : (
                        <>
                          <SelectItem value={OrderType.MARKET_SELL}>
                            {displayStrings.marketSell}
                          </SelectItem>
                          <SelectItem value={OrderType.STOP_LOSS}>
                            {displayStrings.stopLoss}
                          </SelectItem>
                        </>
                      )}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              )}
            />
          )}
          {!isMutualFund && (
            <div className="mt-4">
              <OrderTypeDialog side={side} />
            </div>
          )}
        </div>

        <TradeForm
          asset={asset}
          orderType={orderType}
          setIsAccountValid={setIsAccountValid}
          isMutualFund={isMutualFund}
          isFractionable={isFractionable}
          tradeBreakdown={tradeBreakdown}
        />

        {isMutualFund && side === TradeSide.SELL && (
          <div className="mb-4 flex flex-col gap-2">
            <div className="flex items-start gap-2">
              <Image alt="Info icon" width={14} height={14} src={InfoIcon.src} />
              <WebCaption12.Regular className="text-grey41">
                {displayStrings.withdrawalThreshold(
                  rawThousandFormatNumbro(tradableInCash * 0.9).toString(),
                )}
              </WebCaption12.Regular>
            </div>
            <WebCaption12.Regular className="text-grey41 ml-6">
              {displayStrings.withdrawalPortfolioNote}
            </WebCaption12.Regular>
          </div>
        )}
      </div>
      <SheetFooter className="text-center">
        <div className="flex w-full flex-col">
          {isMutualFund && (
            <>
              {mfDetails && orderType === OrderType.MARKET_BUY && (
                <MutualFundDetailsWidget
                  data={{
                    ...mfDetails,
                    tradableInCash,
                  }}
                  loading={loading || loadingMfSecurityData}
                  minimumTradeAmount={minimumTradeAmount}
                  amount={grossOrderAmount}
                  assetUid={asset?.id}
                />
              )}
              {orderType === OrderType.MARKET_BUY && (
                <div className="mb-7 mt-7 flex flex-col gap-1">
                  <WebCaption12.Regular className="text-grey55">
                    {t('miscScreens.securities.mutualFund.disclosure.line1')}
                    <a
                      href={t('miscScreens.securities.mutualFund.disclosure.searchMutualFundsLink')}
                      className="text-blue underline"
                      target="_blank"
                    >
                      &nbsp;
                      {t('miscScreens.securities.mutualFund.disclosure.searchMutualFundsButton')}
                    </a>
                  </WebCaption12.Regular>
                  <WebCaption12.Regular className="text-grey55">
                    {t('miscScreens.tradeDisplay.tradeScreenMutualFundOrderTimeDisclosure')}
                  </WebCaption12.Regular>
                </div>
              )}
            </>
          )}
          <Button disabled={isContinueDisabled} onClick={handleOnContinue}>
            {displayStrings.continue}
          </Button>
        </div>
      </SheetFooter>
    </SheetContent>
  );
};
